/**
 * LazyImage Component
 * Provides lazy loading with IntersectionObserver, placeholder, and error fallback
 */

import { useState, useRef, useEffect } from 'react';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholderClassName?: string;
  fallback?: React.ReactNode;
  width?: number;
  height?: number;
  /** Root margin for IntersectionObserver (e.g., "100px" to preload 100px before visible) */
  rootMargin?: string;
}

export function LazyImage({
  src,
  alt,
  className = '',
  placeholderClassName = '',
  fallback,
  width,
  height,
  rootMargin = '100px',
}: LazyImageProps): JSX.Element {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = imgRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin,
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin]);

  const handleLoad = (): void => {
    setIsLoaded(true);
  };

  const handleError = (): void => {
    setHasError(true);
  };

  // Show fallback on error
  if (hasError && fallback) {
    return <>{fallback}</>;
  }

  return (
    <div ref={imgRef} className={`relative ${className}`} style={{ width, height }}>
      {/* Placeholder/Skeleton */}
      {!isLoaded && (
        <div
          className={`absolute inset-0 bg-charcoal-100 animate-pulse rounded-inherit ${placeholderClassName}`}
          aria-hidden="true"
        />
      )}

      {/* Actual image (only rendered when in view) */}
      {isInView && (
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading="lazy"
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className={`transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          } ${className}`}
          style={{ width, height }}
        />
      )}
    </div>
  );
}

/**
 * AgencyLogo Component
 * Specialized lazy-loading component for agency logos with emoji fallback
 */
interface AgencyLogoProps {
  src?: string | null;
  alt: string;
  fallbackEmoji: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  xs: 'w-6 h-6 text-sm',
  sm: 'w-10 h-10 text-lg',
  md: 'w-14 h-14 text-2xl',
  lg: 'w-20 h-20 text-3xl',
};

export function AgencyLogo({
  src,
  alt,
  fallbackEmoji,
  size = 'md',
  className = '',
}: AgencyLogoProps): JSX.Element {
  const sizeClass = sizeClasses[size];

  // If no src provided, show emoji fallback directly
  if (!src) {
    return (
      <div
        className={`bg-cherry-100 rounded-xl flex items-center justify-center shrink-0 ${sizeClass} ${className}`}
        role="img"
        aria-label={alt}
      >
        {fallbackEmoji}
      </div>
    );
  }

  // With src, use lazy loading
  return (
    <LazyImage
      src={src}
      alt={alt}
      className={`rounded-xl object-cover shrink-0 ${sizeClass} ${className}`}
      placeholderClassName="rounded-xl"
      fallback={
        <div
          className={`bg-cherry-100 rounded-xl flex items-center justify-center shrink-0 ${sizeClass} ${className}`}
          role="img"
          aria-label={alt}
        >
          {fallbackEmoji}
        </div>
      }
      rootMargin="200px"
    />
  );
}

/**
 * UserAvatar Component
 * Displays user profile photo with initials fallback
 * Supports MS Graph profile photos (data URLs) or initials as fallback
 */
interface UserAvatarProps {
  /** User's avatar - can be a data URL (for MS Graph photo) or initials string */
  avatar?: string | null;
  /** User's display name for alt text and initials generation */
  displayName: string;
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

const avatarSizeClasses = {
  xs: 'w-4 h-4 text-[10px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-11 h-11 text-sm',
  lg: 'w-16 h-16 text-lg',
};

export function UserAvatar({
  avatar,
  displayName,
  size = 'md',
  className = '',
}: UserAvatarProps): JSX.Element {
  const sizeClass = avatarSizeClasses[size];

  // Check if avatar is a data URL (photo) or just initials
  const isPhotoUrl = avatar?.startsWith('data:') || avatar?.startsWith('http');

  // Generate initials from display name if needed
  const initials = avatar && !isPhotoUrl
    ? avatar // Already initials
    : displayName
        .split(' ')
        .map((part) => part[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

  // Use rounded-full for all avatar sizes for consistent circular appearance
  const roundedClass = 'rounded-full';

  // If we have a photo URL, show the image
  if (isPhotoUrl && avatar) {
    return (
      <div className={`relative ${sizeClass} ${className}`}>
        <img
          src={avatar}
          alt={displayName}
          className={`${sizeClass} ${roundedClass} object-cover bg-cherry-100`}
          onError={(e) => {
            // On error, hide image and show initials
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }}
        />
        <div
          className={`hidden ${sizeClass} bg-cherry-100 text-cherry-700 ${roundedClass} flex items-center justify-center font-bold absolute inset-0`}
          role="img"
          aria-label={displayName}
        >
          {initials}
        </div>
      </div>
    );
  }

  // Otherwise show initials
  return (
    <div
      className={`${sizeClass} bg-cherry-100 text-cherry-700 ${roundedClass} flex items-center justify-center font-bold ${className}`}
      role="img"
      aria-label={displayName}
    >
      {initials}
    </div>
  );
}
